// import {BaseNameResponse, SupportCategoryModel, SupportPostModel} from '@services/back-office';
import {BaseNameResponse, SupportCategoryModel, SupportPostModel} from '@services/back-office';
import {OpenedNodePath, SupportRootCategoryModel} from '@support/models/categories-tree.models';
import {SupportCategoryCommonModel} from '@support/components/knowledge-base/category-tree/category-tree.component';

const sortByTitle = (list: {title?: string}[]) => {
    return list.sort((a, b) => a.title!.localeCompare(b.title!, 'uk'));
};

export class TreeHelperService {
    public static getRootForNode(postsTree: SupportCategoryModel[], node?: BaseNameResponse): SupportRootCategoryModel | undefined {
        return TreeHelperService.getPathForNode(postsTree, node)?.rootCategory;
    }

    public static getParentForNode(postsTree: SupportCategoryModel[], node?: BaseNameResponse): SupportCategoryModel | undefined {
        const path = TreeHelperService.getPathForNode(postsTree, node);
        if (!path) return undefined;

        if (path.post) {
            return path.subCategory ?? path.category;
        }
        if (path.subCategory) {
            return path.category;
        }
        if (path.category) {
            return path.rootCategory;
        }

        return undefined;
    }

    public static getPathForNode(postsTree: SupportCategoryCommonModel[], node?: BaseNameResponse): OpenedNodePath | undefined {
        if (!postsTree?.length || !node?.id) return undefined;

        const nodeId = node.id;

        const rootMatch = postsTree.find(root => root.id === nodeId);
        if (rootMatch) {
            return { rootCategory: rootMatch };
        }

        for (const root of postsTree) {
            for (const cat of root.subCategories ?? []) {
                const postInCat = cat.supportPosts?.find(p => p.id === nodeId);
                if (postInCat) return { rootCategory: root, category: cat, post: postInCat };

                for (const sub of (cat as SupportCategoryCommonModel).subCategories ?? []) {
                    const postInSub = sub.supportPosts?.find(p => p.id === nodeId);
                    if (postInSub) {
                        return { rootCategory: root, category: cat, subCategory: sub, post: postInSub };
                    }
                }
            }
        }

        for (const root of postsTree) {
            const cat = root.subCategories?.find(c => c.id === nodeId);
            if (cat) return { rootCategory: root, category: cat };

            for (const sub of root.subCategories ?? []) {
                const subCategory = (sub as SupportCategoryCommonModel).subCategories?.find(s => s.id === nodeId);
                if (subCategory) {
                    return { rootCategory: root, category: sub, subCategory };
                }
            }
        }

        return undefined;
    }

    public static removeNodeFromTree(tree: SupportCategoryCommonModel[], targetId: string): SupportCategoryCommonModel[] {
        function walk(categories: SupportCategoryCommonModel[]): SupportCategoryCommonModel[] {
            const result: SupportCategoryCommonModel[] = [];

            for (const cat of categories) {
                if (cat.id === targetId) continue;

                const filteredPosts = cat.supportPosts
                    ? cat.supportPosts.filter(p => p.id !== targetId)
                    : undefined;

                const filteredSubs = cat.subCategories
                    ? walk(cat.subCategories)
                    : undefined;

                result.push({
                    ...cat,
                    supportPosts: filteredPosts,
                    subCategories: filteredSubs
                });
            }

            return result;
        }

        return walk(tree);
    }

    public static upsertPostInTree(
        tree: SupportCategoryCommonModel[],
        post: SupportPostModel
    ): SupportCategoryCommonModel[] {
        const targetCategoryId = post.category?.id;

        if (!targetCategoryId) {
            return tree;
        }

        let inserted = false;

        const walk = (
            categories: SupportCategoryCommonModel[] | undefined
        ): SupportCategoryCommonModel[] | undefined => {
            if (!categories?.length) {
                return categories;
            }

            return categories.map(category => {
                let supportPosts = category.supportPosts;
                let subCategories = walk(category.subCategories);

                if (!inserted && category.id === targetCategoryId) {
                    supportPosts = sortByTitle([
                        ...(category.supportPosts ?? []).filter(p => p.id !== post.id),
                        post
                    ]);

                    inserted = true;
                }

                return {
                    ...category,
                    supportPosts,
                    subCategories
                };
            });
        };

        const updatedTree = walk(tree);

        return inserted ? updatedTree ?? tree : tree;
    }
}
